import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { laneLabel, prettify, formatDateTime, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PortalUpload } from "@/components/PortalUpload";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const ResIcon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C {...p} />;
};

const InfoTile = ({ icon: Icon, label, value, accent }) => (
  <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="family-info-tile">
    <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: accent || "rgba(28,67,88,0.10)" }}>
      <Icon size={20} className="text-[#1C4358]" />
    </div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-lg font-display font-bold text-[#252221] mt-0.5">{value || "—"}</div>
  </Card>
);

export default function FamilyPortal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ preferred_time: "", goals: "", questions: "" });
  const [savingForm, setSavingForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/family/overview");
      setData(data);
    } catch {
      toast.error("Could not load your dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmAppt = async (id) => {
    setConfirming(true);
    try {
      await api.post(`/portal/appointments/${id}/confirm`);
      toast.success("Appointment confirmed — thank you!");
      load();
    } catch {
      toast.error("Could not confirm appointment");
    } finally {
      setConfirming(false);
    }
  };

  const submitForm = async () => {
    setSavingForm(true);
    try {
      await api.post("/portal/forms", { form_type: "Family check-in", fields: form });
      toast.success("Thank you — your coordinator will see this.");
      setFormOpen(false);
      setForm({ preferred_time: "", goals: "", questions: "" });
    } catch {
      toast.error("Could not submit form");
    } finally {
      setSavingForm(false);
    }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-28" /><div className="grid grid-cols-3 gap-4"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div><Skeleton className="h-40" /></div>;

  if (!data?.family) {
    return (
      <Card className="p-12 text-center border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="family-empty">
        <Icons.HeartHandshake size={28} className="mx-auto text-[#1C4358] mb-3" />
        <p className="font-medium text-[#252221]">Welcome to your Family Portal</p>
        <p className="text-sm text-muted-foreground">Your coordinator is setting things up. Please check back soon.</p>
      </Card>
    );
  }

  const { family, loved_one_name, coordinator_name, case: kase, next_appointment, next_steps,
    documents_requested, checklist, action_plan_docs, action_plan_note, resources } = data;
  const doneCount = (checklist || []).filter((c) => c.done).length;
  const pct = checklist?.length ? Math.round((doneCount / checklist.length) * 100) : 0;
  const greetName = family.primary_contact_name || family.family_name;

  return (
    <div className="space-y-6" data-testid="family-portal">
      {/* Greeting hero */}
      <div className="rounded-2xl p-6 sm:p-7 border border-[#E6EDF2]" style={{ background: "linear-gradient(180deg, rgba(155,185,206,0.20), rgba(155,185,206,0.06))" }}>
        <div className="flex items-center gap-2 text-[#1C4358] mb-1"><Icons.Sun size={18} /><span className="text-sm font-medium">Welcome back</span></div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#252221]" data-testid="family-greeting">Hello, {greetName}</h2>
        <p className="text-[15px] text-[#4B5563] mt-1 leading-relaxed">
          We're walking alongside you on {loved_one_name ? <span className="font-semibold text-[#1C4358]">{loved_one_name}</span> : "your loved one"}'s journey. Here's where things stand today.
        </p>
      </div>

      {/* Info tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <InfoTile icon={Icons.Heart} label="Your loved one" value={loved_one_name} accent="rgba(156,30,34,0.10)" />
        <InfoTile icon={Icons.UserRound} label="Your coordinator" value={coordinator_name} />
        <InfoTile icon={Icons.Compass} label="Current step" value={kase ? prettify(kase.case_status) : "Getting started"} accent="rgba(196,200,155,0.30)" />
      </div>

      {kase && (
        <Card className="p-5 border-[#E6EDF2] flex items-center justify-between gap-3" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.10)" }}><Icons.FolderHeart size={20} className="text-[#1C4358]" /></div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#252221] truncate" data-testid="family-service-lane">{laneLabel(kase.service_lane)}</div>
              <div className="text-xs text-muted-foreground">Case {kase.case_number}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Next appointment */}
      <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="family-next-appointment">
        <h3 className="font-display font-semibold text-[#252221] mb-3 flex items-center gap-2"><Icons.CalendarClock size={18} className="text-[#1C4358]" /> Next appointment</h3>
        {next_appointment ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[15px] font-medium text-[#252221]">{formatDateTime(next_appointment.scheduled_at)}</div>
              <div className="text-sm text-muted-foreground">{prettify(next_appointment.mode || "Meeting")}{next_appointment.location ? ` · ${next_appointment.location}` : ""}</div>
            </div>
            {next_appointment.family_confirmed ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#256B4B]" data-testid="family-appt-confirmed"><Icons.CheckCircle2 size={16} /> Confirmed</span>
            ) : (
              <Button onClick={() => confirmAppt(next_appointment.id)} disabled={confirming} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="family-confirm-appt-button">
                {confirming ? <Icons.Loader2 size={16} className="mr-2 animate-spin" /> : <Icons.Check size={16} className="mr-2" />} Confirm
              </Button>
            )}
          </div>
        ) : <p className="text-sm text-muted-foreground">No appointment scheduled yet. Your coordinator will reach out soon.</p>}
      </Card>

      {/* Next steps + Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="family-next-steps">
          <h3 className="font-display font-semibold text-[#252221] mb-3 flex items-center gap-2"><Icons.Footprints size={18} className="text-[#1C4358]" /> Your next steps</h3>
          <ul className="space-y-2.5">
            {(next_steps || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[15px] text-[#252221]">
                <Icons.ArrowRightCircle size={18} className="text-[#9C1E22] mt-0.5 shrink-0" /><span className="leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="family-checklist">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-[#252221] flex items-center gap-2"><Icons.ListChecks size={18} className="text-[#1C4358]" /> Family plan</h3>
            <span className="text-sm font-semibold text-[#1C4358] tabular-nums">{doneCount}/{checklist?.length || 0}</span>
          </div>
          <Progress value={pct} className="h-2 mb-4" />
          <ul className="space-y-2">
            {(checklist || []).map((c) => (
              <li key={c.id} className="flex items-center gap-2.5 text-sm">
                {c.done ? <Icons.CheckCircle2 size={17} className="text-[#2F7D5A] shrink-0" /> : <Icons.Circle size={17} className="text-[#9BB9CE] shrink-0" />}
                <span className={c.done ? "text-muted-foreground line-through" : "text-[#252221]"}>{c.label}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Documents requested + actions */}
      <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="family-documents-requested">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="font-display font-semibold text-[#252221] flex items-center gap-2"><Icons.FileWarning size={18} className="text-[#1C4358]" /> Documents requested</h3>
          <div className="flex items-center gap-2">
            <PortalUpload endpoint="/portal/documents/upload" label="Upload a document" testid="family-upload-button" onUploaded={load} />
            <Button variant="outline" onClick={() => navigate("/portal/documents")} data-testid="family-view-documents">View all</Button>
          </div>
        </div>
        {(documents_requested || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents are needed right now. Thank you!</p>
        ) : (
          <ul className="divide-y divide-[#F0F4F7]">
            {documents_requested.map((d) => (
              <li key={d.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5"><Icons.FileText size={16} className="text-[#9C1E22]" /><span className="text-sm text-[#252221]">{d.title}</span></div>
                {d.due_date && <span className="text-xs text-muted-foreground">Due {formatDate(d.due_date)}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Family action plan */}
      {(action_plan_note || (action_plan_docs && action_plan_docs.length > 0)) && (
        <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="family-action-plan">
          <h3 className="font-display font-semibold text-[#252221] mb-3 flex items-center gap-2"><Icons.ClipboardCheck size={18} className="text-[#1C4358]" /> Your family action plan</h3>
          {action_plan_note && <p className="text-[15px] text-[#252221] leading-relaxed whitespace-pre-wrap mb-3">{action_plan_note.body}</p>}
          {(action_plan_docs || []).map((d) => (
            <div key={d.id} className="flex items-center gap-2.5 text-sm text-[#1C4358]"><Icons.FileText size={16} /> {d.original_filename}</div>
          ))}
        </Card>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button onClick={() => navigate("/portal/messages")} className="text-left rounded-xl border border-[#E6EDF2] bg-white p-4 hover:bg-[rgba(155,185,206,0.10)] transition-colors" data-testid="family-action-message">
          <Icons.MessagesSquare size={20} className="text-[#1C4358] mb-2" />
          <div className="text-sm font-semibold text-[#252221]">Message your coordinator</div>
          <div className="text-xs text-muted-foreground">Ask a question any time</div>
        </button>
        <button onClick={() => setFormOpen(true)} className="text-left rounded-xl border border-[#E6EDF2] bg-white p-4 hover:bg-[rgba(155,185,206,0.10)] transition-colors" data-testid="family-action-form">
          <Icons.ClipboardList size={20} className="text-[#1C4358] mb-2" />
          <div className="text-sm font-semibold text-[#252221]">Complete a quick form</div>
          <div className="text-xs text-muted-foreground">Share goals &amp; availability</div>
        </button>
        <button onClick={() => navigate("/portal/documents")} className="text-left rounded-xl border border-[#E6EDF2] bg-white p-4 hover:bg-[rgba(155,185,206,0.10)] transition-colors" data-testid="family-action-documents">
          <Icons.FolderOpen size={20} className="text-[#1C4358] mb-2" />
          <div className="text-sm font-semibold text-[#252221]">Documents &amp; reports</div>
          <div className="text-xs text-muted-foreground">Upload or download files</div>
        </button>
      </div>

      {/* Helpful resources */}
      <div>
        <h3 className="font-display font-semibold text-[#252221] mb-3 flex items-center gap-2"><Icons.LifeBuoy size={18} className="text-[#1C4358]" /> Helpful resources</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(resources || []).map((r, i) => (
            <Card key={i} className="p-4 border-[#E6EDF2] flex items-start gap-3" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="family-resource">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(155,185,206,0.22)" }}><ResIcon name={r.icon} size={18} className="text-[#1C4358]" /></div>
              <div><div className="text-sm font-semibold text-[#252221]">{r.title}</div><div className="text-xs text-muted-foreground leading-relaxed">{r.description}</div></div>
            </Card>
          ))}
        </div>
      </div>

      {/* Form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-md" data-testid="family-form-dialog">
          <DialogHeader><DialogTitle>Quick family check-in</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Best time to reach you</Label><Input value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} placeholder="e.g. Weekday mornings" data-testid="family-form-time" /></div>
            <div><Label className="text-sm">Your goals right now</Label><Textarea value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} rows={2} placeholder="What matters most to your family?" data-testid="family-form-goals" /></div>
            <div><Label className="text-sm">Any questions for us?</Label><Textarea value={form.questions} onChange={(e) => setForm({ ...form, questions: e.target.value })} rows={2} placeholder="We're here to help" data-testid="family-form-questions" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={submitForm} disabled={savingForm} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="family-form-submit">
              {savingForm ? <Icons.Loader2 size={16} className="mr-2 animate-spin" /> : null} Send to coordinator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
